// src/component/PaymentStatus2026.js
import React, { useEffect, useState, useContext } from "react";
import { Box, Typography, CircularProgress, Chip } from "@mui/material";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db, auth } from "../api/firebase";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { AppThemeContext } from "./AppShell";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";

/* ==========================================================
   🔧 ヘルパー関数たち
   ========================================================== */

// Firestore Timestamp / Date / string にゆるく対応
function normalizeTimestamp(ts) {
  if (!ts) return null;
  if (ts.toDate) {
    try {
      return ts.toDate();
    } catch {
      return null;
    }
  }
  if (ts instanceof Date) return ts;
  if (typeof ts === "string") {
    const d = new Date(ts);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

// 日付を "YYYY-MM-DD" にそろえる
function formatDateKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// グラフ用データ作成（日付ごとに card/bank カウント）
function buildChartData(participants) {
  const map = {};

  participants.forEach((p) => {
    const d = p.joinedAt;
    const key = d ? formatDateKey(d) : "不明";

    if (!map[key]) {
      map[key] = { date: key, card: 0, bank: 0 };
    }

    if (p.methodType === "bank") {
      map[key].bank += 1;
    } else {
      map[key].card += 1;
    }
  });

  return Object.values(map).sort((a, b) => (a.date > b.date ? 1 : -1));
}

// 選択された日付について、時間帯ごとの card/bank 人数を作る
function buildHourlyChartData(participants, selectedDate) {
  if (!selectedDate) return [];

  const map = {};

  participants.forEach((p) => {
    if (!p.joinedAt) return;
    const dateKey = formatDateKey(p.joinedAt);
    if (dateKey !== selectedDate) return;

    const hour = p.joinedAt.getHours(); // 0〜23
    const label = `${String(hour).padStart(2, "0")}:00`;

    if (!map[label]) {
      map[label] = { hour: label, card: 0, bank: 0 };
    }

    if (p.methodType === "bank") {
      map[label].bank += 1;
    } else {
      map[label].card += 1;
    }
  });

  return Object.values(map).sort((a, b) => (a.hour > b.hour ? 1 : -1));
}

/* ==========================================================
   👑 メインコンポーネント
   ========================================================== */

const PaymentStatus2026 = () => {
  const navigate = useNavigate();
  const theme = useContext(AppThemeContext) || {};

  const surface = theme.surfaceBg ?? "#FFF";
  const textBase = theme.railText ?? "rgba(40,40,60,0.9)";

  const [isAdmin, setIsAdmin] = useState(null); // null = 判定中
  const [loading, setLoading] = useState(true);

  const [participants, setParticipants] = useState([]);
  const [chartData, setChartData] = useState([]);

  const [selectedDate, setSelectedDate] = useState(null); // "YYYY-MM-DD"
  const [selectedHour, setSelectedHour] = useState(null); // "HH:00"
  const [hourlyChartData, setHourlyChartData] = useState([]);

  /* ----------------------------------------------------------
     管理者チェック
     ---------------------------------------------------------- */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate("/home");
        return;
      }

      try {
        const ref = doc(db, "users", user.uid);
        const snap = await getDoc(ref);
        const data = snap.data();
        if (!snap.exists() || !data?.isAdmin) {
          navigate("/home");
          return;
        }
        setIsAdmin(true);
      } catch (e) {
        console.error("admin check error:", e);
        navigate("/home");
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  /* ----------------------------------------------------------
     users から 4期支払い者を抽出
     ---------------------------------------------------------- */
  useEffect(() => {
    const fetchData = async () => {
      try {
        const snap = await getDocs(collection(db, "users"));

        const list = [];

        snap.forEach((docSnap) => {
          const data = docSnap.data();
          const payments = Array.isArray(data.payments) ? data.payments : [];

          // paymentPhase === "4" の支払いを抽出（現状ロジック踏襲）
          const phase4Payments = payments.filter(
            (p) => p && p.paymentPhase === "4"
          );
          if (phase4Payments.length === 0) return;

          // 4期以外の支払いがあるか？ → 継続判定に使う
          const hasOtherPhase = payments.some(
            (p) => p && p.paymentPhase && p.paymentPhase !== "4"
          );
          const customerType = hasOtherPhase ? "continuing" : "new";

          // 4期に入った日（最初の timestamp）
          const sorted = [...phase4Payments].sort((a, b) => {
            const ta = normalizeTimestamp(a.timestamp)?.getTime() ?? 0;
            const tb = normalizeTimestamp(b.timestamp)?.getTime() ?? 0;
            return ta - tb;
          });

          const first = sorted[0];
          const joinedAt = normalizeTimestamp(first.timestamp);

          const rawMethod = first.paymentMethod || "";
          const methodType = rawMethod.includes("振込") ? "bank" : "card";

          list.push({
            userId: docSnap.id,
            methodType, // "card" | "bank"
            joinedAt,
            customerType, // "new" | "continuing"
          });
        });

        // joinedAt の古い順にソート
        list.sort((a, b) => {
          const ta = a.joinedAt?.getTime() ?? 0;
          const tb = b.joinedAt?.getTime() ?? 0;
          return ta - tb;
        });

        setParticipants(list);
        setChartData(buildChartData(list));
      } catch (e) {
        console.error("PaymentStatus2026 fetch error:", e);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  /* ----------------------------------------------------------
     選択された日付が変わったら時間帯グラフ用データを更新
     ---------------------------------------------------------- */
  useEffect(() => {
    if (!selectedDate) {
      setHourlyChartData([]);
      setSelectedHour(null);
      return;
    }
    setHourlyChartData(buildHourlyChartData(participants, selectedDate));
    setSelectedHour(null);
  }, [participants, selectedDate]);

  if (isAdmin === null || loading) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAdmin) return null;

  // ---------------------------------------------------------
  //  集計対象 participants をフィルタ（グラフ & チップ共通）
  // ---------------------------------------------------------
  const baseList = participants.filter((p) => {
    if (!p.joinedAt) return false;

    if (selectedDate) {
      const key = formatDateKey(p.joinedAt);
      if (key !== selectedDate) return false;
    }

    if (selectedHour) {
      const hourLabel = `${String(p.joinedAt.getHours()).padStart(2, "0")}:00`;
      if (hourLabel !== selectedHour) return false;
    }

    return true;
  });

  const total = baseList.length;
  const totalCard = baseList.filter((p) => p.methodType === "card").length;
  const totalBank = baseList.filter((p) => p.methodType === "bank").length;

  const totalNew = baseList.filter((p) => p.customerType === "new").length;
  const totalContinuing = baseList.filter((p) => p.customerType === "continuing").length;

  const newRatio = total === 0 ? 0 : Math.round((totalNew / total) * 100);
  const continuingRatio = total === 0 ? 0 : Math.round((totalContinuing / total) * 100);

  const customerTypeChartData = [
    { type: "新規（4期のみ）", count: totalNew },
    { type: "継続（他期あり）", count: totalContinuing },
  ];

  return (
    <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
      {/* ヘッダー */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="overline"
          sx={{
            letterSpacing: "0.18em",
            fontSize: 11,
            opacity: 0.8,
            color: textBase,
          }}
        >
          ADMIN / MM2026
        </Typography>

        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            letterSpacing: "0.03em",
            color: textBase,
          }}
        >
          2026支払状況
        </Typography>

        <Typography variant="body2" sx={{ mt: 0.5, color: "rgba(0,0,0,0.6)" }}>
          第4期（MM2026）の参加者を支払い方法・参加日・新規/継続ごとに確認できます。
        </Typography>

        <Box sx={{ mt: 1.5, display: "flex", gap: 1.2, flexWrap: "wrap" }}>
          <Chip label={`合計 ${total} 人`} color="primary" variant="outlined" size="small" />
          <Chip label={`クレジット/その他: ${totalCard} 人`} size="small" />
          <Chip label={`銀行振込: ${totalBank} 人`} size="small" />
          <Chip label={`新規: ${totalNew} 人（${newRatio}%）`} size="small" />
          <Chip label={`継続: ${totalContinuing} 人（${continuingRatio}%）`} size="small" />
        </Box>

        <Box sx={{ mt: 1 }}>
          {selectedDate ? (
            selectedHour ? (
              <Typography variant="body2" color="text.secondary">
                表示中: <strong>{selectedDate} {selectedHour}</strong>（同じ棒を再クリックで解除）
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                表示中: <strong>{selectedDate}</strong>（日付の棒を再クリックで解除）
              </Typography>
            )
          ) : (
            <Typography variant="body2" color="text.secondary">
              表示中: すべて
            </Typography>
          )}
        </Box>
      </Box>

      {/* 日別グラフ */}
      <Box
        sx={{
          mb: 4,
          p: 2,
          borderRadius: 3,
          bgcolor: surface,
          boxShadow: "0 12px 30px rgba(0,0,0,0.08)",
          height: 320,
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 700, color: textBase }}>
          日別・支払い方法別 参加人数
        </Typography>

        {chartData.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            まだ第4期の支払いデータがありません。
          </Typography>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar
                dataKey="card"
                name="クレジット/その他"
                stackId="a"
                fill="#4A90E2"
                onClick={(data) => {
                  const dateKey = data?.payload?.date;
                  if (!dateKey) return;
                  setSelectedDate((prev) => (prev === dateKey ? null : dateKey));
                  setSelectedHour(null);
                }}
              />
              <Bar
                dataKey="bank"
                name="銀行振込"
                stackId="a"
                fill="#F5A623"
                onClick={(data) => {
                  const dateKey = data?.payload?.date;
                  if (!dateKey) return;
                  setSelectedDate((prev) => (prev === dateKey ? null : dateKey));
                  setSelectedHour(null);
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>

      {/* 時間帯別グラフ（選択日があるときだけ表示） */}
      {selectedDate && (
        <Box
          sx={{
            mb: 4,
            p: 2,
            borderRadius: 3,
            bgcolor: surface,
            boxShadow: "0 12px 30px rgba(0,0,0,0.08)",
            height: 320,
          }}
        >
          <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 700, color: textBase }}>
            {selectedDate} の時間帯別 参加人数
          </Typography>

          {hourlyChartData.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              この日付には参加データがありません。
            </Typography>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hourlyChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar
                  dataKey="card"
                  name="クレジット/その他"
                  stackId="a"
                  fill="#4A90E2"
                  onClick={(data) => {
                    const hourKey = data?.payload?.hour;
                    if (!hourKey) return;
                    setSelectedHour((prev) => (prev === hourKey ? null : hourKey));
                  }}
                />
                <Bar
                  dataKey="bank"
                  name="銀行振込"
                  stackId="a"
                  fill="#F5A623"
                  onClick={(data) => {
                    const hourKey = data?.payload?.hour;
                    if (!hourKey) return;
                    setSelectedHour((prev) => (prev === hourKey ? null : hourKey));
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Box>
      )}

      {/* 新規 vs 継続 */}
      <Box
        sx={{
          mb: 2,
          p: 2,
          borderRadius: 3,
          bgcolor: surface,
          boxShadow: "0 12px 30px rgba(0,0,0,0.08)",
          height: 260,
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 700, color: textBase }}>
          新規 / 継続 の人数（4期参加者）
        </Typography>

        {total === 0 ? (
          <Typography variant="body2" color="text.secondary">
            対象範囲の参加者データがありません。
          </Typography>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={customerTypeChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="type" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" name="人数" fill="#7B61FF" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Box>
  );
};

export default PaymentStatus2026;
